import React, { useState, useRef } from "react";
import { Upload, Download, X, CheckCircle, AlertCircle, FileSpreadsheet } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { toast } from "sonner";
import api from "../utils/api";

const CONFIG = {
  customers: {
    label: "Clientes",
    endpoint: "/import/customers",
    templateEndpoint: "/import/template/customers",
    columns: ["nombre", "rfc", "email", "telefono", "direccion", "nombre_contacto", "dias_credito", "notas"],
    example: ["Juan Pérez", "PEPJ800101ABC", "juan@email.com", "5512345678", "Calle 123", "María", "30", "Cliente frecuente"],
  },
  suppliers: {
    label: "Proveedores",
    endpoint: "/import/suppliers",
    templateEndpoint: "/import/template/suppliers",
    columns: ["nombre", "rfc", "email", "telefono", "direccion", "nombre_contacto", "dias_credito", "notas"],
    example: ["Proveedor SA", "PRSA900101XYZ", "proveedor@email.com", "5598765432", "Av. Principal 456", "Carlos", "15", "Proveedor principal"],
  },
  products: {
    label: "Productos",
    endpoint: "/import/products",
    templateEndpoint: "/import/template/products",
    columns: ["nombre", "sku", "categoria", "unidad", "costo", "precio", "stock_minimo", "descripcion"],
    example: ["Hígado de Res Deshidratado", "HRD-001", "producto_final", "kg", "120.00", "250.00", "5", "Snack para perros"],
    notes: "Categorías válidas: producto_final, insumo_directo, insumo_indirecto. Unidades: kg, g, pza, lt, ml, bolsa, caja, rollo",
  },
};

export default function ImportModal({ type, onClose, onSuccess }) {
  const config = CONFIG[type];
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef();

  const handleFile = (f) => {
    if (!f) return;
    const ext = f.name.split(".").pop().toLowerCase();
    if (!["xlsx", "xls", "csv"].includes(ext)) {
      toast.error("Solo se aceptan archivos .xlsx, .xls o .csv");
      return;
    }
    setFile(f);
    setResult(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    handleFile(e.dataTransfer.files[0]);
  };

  const handleUpload = async () => {
    if (!file) { toast.error("Selecciona un archivo primero"); return; }
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post(config.endpoint, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(res.data);
      const inserted = res.data.inserted || 0;
      const updated = res.data.updated || 0;
      const parts = [];
      if (inserted > 0) parts.push(`${inserted} creados`);
      if (updated > 0) parts.push(`${updated} actualizados`);
      if (inserted > 0 || updated > 0) {
        toast.success(`Importación: ${parts.join(", ")}`);
        onSuccess && onSuccess();
      } else {
        toast.info("Sin cambios: todos los registros ya existen");
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(detail || "Error al importar el archivo");
      setResult({ error: detail || "Error desconocido", inserted: 0, errors: [] });
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await api.get(config.templateEndpoint, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `plantilla_${type}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Error al descargar la plantilla");
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet size={18} className="text-brand-600" />
            Importar {config.label} desde Excel
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-1">
          {/* Download template */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-3">
            <div className="flex-1">
              <p className="text-sm font-medium text-blue-800">¿Primera vez? Descarga la plantilla</p>
              <p className="text-xs text-blue-600 mt-0.5">
                Columnas: {config.columns.join(", ")}
              </p>
              {config.notes && <p className="text-xs text-blue-500 mt-1">{config.notes}</p>}
            </div>
            <button
              onClick={handleDownloadTemplate}
              className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg text-xs font-medium transition-colors whitespace-nowrap"
            >
              <Download size={13} /> Plantilla
            </button>
          </div>

          {/* Drop zone */}
          <div
            onClick={() => fileRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
              dragging ? "border-brand-400 bg-brand-50" : file ? "border-brand-300 bg-brand-50" : "border-slate-200 hover:border-brand-300 hover:bg-slate-50"
            }`}
          >
            <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={(e) => handleFile(e.target.files[0])} />
            {file ? (
              <div className="flex flex-col items-center gap-2">
                <FileSpreadsheet size={32} className="text-brand-500" />
                <p className="text-sm font-medium text-brand-700">{file.name}</p>
                <p className="text-xs text-slate-400">{(file.size / 1024).toFixed(1)} KB</p>
                <button
                  onClick={(e) => { e.stopPropagation(); setFile(null); setResult(null); }}
                  className="text-xs text-red-400 hover:text-red-600 flex items-center gap-1 mt-1"
                >
                  <X size={12} /> Cambiar archivo
                </button>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <Upload size={32} className="text-slate-300" />
                <p className="text-sm text-slate-500">Arrastra tu archivo aquí o <span className="text-brand-600 font-medium">haz clic para seleccionar</span></p>
                <p className="text-xs text-slate-400">Formatos: .xlsx, .xls, .csv</p>
              </div>
            )}
          </div>

          {/* Result summary */}
          {result && (
            <div className={`rounded-lg p-3 border ${(result.inserted > 0 || result.updated > 0) ? "bg-brand-50 border-brand-200" : "bg-amber-50 border-amber-200"}`}>
              {(result.inserted > 0 || result.updated > 0) && (
                <div className="flex items-center gap-2 text-brand-700 font-medium text-sm mb-1">
                  <CheckCircle size={15} />
                  {[result.inserted > 0 && `${result.inserted} creados`, result.updated > 0 && `${result.updated} actualizados`].filter(Boolean).join(", ")}
                </div>
              )}
              {result.errors?.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 text-amber-700 font-medium text-xs mb-1">
                    <AlertCircle size={13} /> {result.errors.length} errores encontrados:
                  </div>
                  <ul className="text-xs text-amber-700 space-y-0.5 max-h-28 overflow-y-auto">
                    {result.errors.map((e, i) => <li key={i}>• {e}</li>)}
                  </ul>
                </div>
              )}
              {result.error && <p className="text-sm text-red-700">{result.error}</p>}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 pt-1">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">
            Cerrar
          </button>
          <button
            onClick={handleUpload}
            disabled={!file || loading}
            className="flex items-center gap-2 px-5 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
          >
            {loading ? (
              <>
                <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Importando...
              </>
            ) : (
              <><Upload size={14} /> Importar</>
            )}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
